import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../../AuthContext';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [userId, setUserId] = useState("");
  const [pendingRequest, setPendingRequest] = useState(0);
  const [liveRequest, setLiveRequest] = useState(0);
  const [liveMessages, setLiveMessages] = useState(0);
  const [pendingMessages, setPendingMessages] = useState(0);

  // Get auth context
  const { loggedIn, user } = useContext(AuthContext);

  const fetchUnreadCount = async () => {
    if (!loggedIn || !user?.id) return; // Add user?.id check

    try {
        const response = await axios.get('/api/messages/unread/all');
        if (response?.data) {  // Add null check
            setUnreadMessages(response.data.messages);
            setUserId(response.data.userId);
        }
    } catch (error) {
        if (error.response?.status === 401) {
            window.open('/auth0/logout', '_self');
        }
        console.error("Error fetching unread count", error);
    }
};

  const fetchPendingRequest = async () => {
    if (!loggedIn || !user?.id) return; // Add user?.id check

    try {
      const response = await axios.get('/api/bookings/all/pending/request');
      setPendingRequest(response.data.totalRequests);
      setPendingMessages(response.data.totalMessages);
    } catch (error) {
      if (error.response?.status === 401) {
        window.open('/auth0/logout', '_self');
      }
      console.error("Error fetching pending requests", error);
    }
  };

  const fetchLiveRequest = async () => {
    if (!loggedIn || !user?.id) return; // Add user?.id check

    try {
      const response = await axios.get('/api/bookings/all/live/request');
      setLiveRequest(response.data.totalRequests);
      setLiveMessages(response.data.totalMessages);
    } catch (error) {
      if (error.response?.status === 401) {
        window.open('/auth0/logout', '_self');
      }
      console.error("Error fetching live requests", error);
    }
  };

  const resetNotifications = () => {
    setUnreadMessages(0);
    setUserId("");
    setPendingRequest(0);
    setLiveRequest(0);
    setLiveMessages(0);
    setPendingMessages(0);
  };


  useEffect(() => {
    let mounted = true;

    if (loggedIn && user?.id) {
        const fetchData = async () => {
            try {
                if (mounted) {
                    await fetchUnreadCount();
                    await fetchLiveRequest();
                    await fetchPendingRequest();
                }
            } catch (error) {
                console.error("Error fetching notifications:", error);
            }
        };

        fetchData();
    } else {
        resetNotifications();
    }

    return () => {
        mounted = false;
    };
}, [loggedIn, user?.id]);

  const value = {
    liveMessages,
    pendingMessages,
    liveRequest,
    pendingRequest,
    unreadMessages,
    setUnreadMessages,
    userId,
    fetchUnreadCount,
    refreshNotifications: async () => {
      if (!loggedIn) return;
      await Promise.all([
        fetchUnreadCount(),
        fetchLiveRequest(),
        fetchPendingRequest()
      ]);
    }
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
